import { ScreenLink as Link } from '../permissions/ScreenLink';
import { useTranslation } from 'react-i18next';
import HoursProgressBar from '../HoursProgressBar';
import StatusBadge from '../StatusBadge';
import DetailSection from './DetailSection';
import { formatDate } from '../../utils/format';
import { dueDayPassed } from '../../utils/israelTime';
import type { MilestoneStatus } from '../../types';

export interface MilestoneProgressItem {
  id: string;
  name: string;
  status: MilestoneStatus;
  dueDate?: string;
  ticketsTotal: number;
  ticketsClosed: number;
  estimatedHours: number;
  actualHours: number;
}

/**
 * Flat milestone progress list for a detail page: one section, one row per
 * milestone (name + status, task progress, due date, hours bar). Replaces the
 * card-per-milestone widget on the project page.
 */
export default function MilestoneProgressList({ milestones }: { milestones: MilestoneProgressItem[] }) {
  const { t } = useTranslation();
  return (
    <DetailSection title={t('charts.milestones')} flush>
      {milestones.length === 0 ? (
        <p className="px-4 sm:px-5 py-6 text-sm text-surface-600 text-center">{t('charts.noMilestones')}</p>
      ) : (
        <ul className="divide-y divide-surface-100">
          {milestones.map((ms) => {
            const pct = ms.ticketsTotal > 0 ? Math.round((ms.ticketsClosed / ms.ticketsTotal) * 100) : 0;
            const overdue = dueDayPassed(ms.dueDate) && ms.status !== 'completed';
            return (
              <li key={ms.id}>
                <Link
                  to={`/milestones/${ms.id}`}
                  className="grid grid-cols-[minmax(0,1fr)_auto] items-center gap-x-4 gap-y-1.5 px-4 sm:px-5 py-3 hover:bg-surface-50 transition-colors duration-150"
                >
                  <span className="flex items-center gap-2 min-w-0">
                    <span className="text-sm font-medium text-surface-900 truncate">{ms.name}</span>
                    <StatusBadge status={ms.status} />
                  </span>
                  <span className="text-xs text-surface-600 tabular-nums text-end whitespace-nowrap">
                    {ms.ticketsClosed}/{ms.ticketsTotal} · {pct}%
                  </span>
                  <span className="h-1.5 rounded-full bg-surface-100 overflow-hidden" aria-hidden="true">
                    <span className="block h-full rounded-full bg-primary-600" style={{ width: `${pct}%` }} />
                  </span>
                  <span className={`text-xs tabular-nums text-end whitespace-nowrap ${overdue ? 'text-danger-700 font-medium' : 'text-surface-600'}`}>
                    {ms.dueDate ? formatDate(ms.dueDate) : ''}
                  </span>
                  {ms.estimatedHours > 0 && (
                    <span className="col-span-2">
                      <HoursProgressBar actual={ms.actualHours} estimated={ms.estimatedHours} showLabel={false} />
                    </span>
                  )}
                </Link>
              </li>
            );
          })}
        </ul>
      )}
    </DetailSection>
  );
}
